import { useEffect, useRef } from 'react';
import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import type { PluginListenerHandle } from '@capacitor/core';

/**
 * Die Android-Zurücktaste.
 *
 * Zwei Dinge müssen zusammenkommen, damit sie überhaupt etwas tut:
 *
 * 1. Capacitors App-Plugin schluckt den Tastendruck kommentarlos, solange
 *    **niemand** auf `backButton` hört - die WebView kann nicht zurück
 *    (die App ist eine einzige Seite), also passiert nichts. Erst mit einem
 *    registrierten Zuhörer schickt das Plugin zusätzlich das
 *    `backbutton`-Ereignis an das Dokument, auf das Ionic wartet.
 * 2. Ionic sammelt daraufhin über `ionBackButton` alle Handler ein und ruft
 *    den mit der höchsten Priorität auf. Overlays (Alert, Modal) hängen dort
 *    mit Priorität 100 drin und schliessen sich damit von selbst.
 *
 * Deshalb: `useHardwareBackButton()` einmal in der App aufrufen (Punkt 1),
 * alles Weitere über `useBackButton()` (Punkt 2).
 */

/** Ein Handler entscheidet: selbst erledigen - oder `next()` weiterreichen. */
export type BackHandler = (next: () => void) => void;

interface BackButtonEventDetail {
  register(priority: number, handler: BackHandler): void;
}

/**
 * Die Stufen, auf denen wir mitspielen. Ionics Overlays liegen bei 100 und
 * bleiben damit immer vorn - ein offener Dialog schliesst sich zuerst.
 */
export const BACK_PRIORITY = {
  /** Eigene bildschirmfüllende Ebenen, z. B. die Karte im Vollbild. */
  overlay: 50,
  /** Der Wechsel eine Ansicht zurück. */
  view: 10,
  /** Letzte Instanz: die App verlassen. */
  exit: 0,
} as const;

/** Hängt einen Handler an die Zurücktaste, solange die Komponente lebt. */
export function useBackButton(priority: number, handler: BackHandler) {
  const latest = useRef(handler);

  useEffect(() => {
    latest.current = handler;
  });

  useEffect(() => {
    const onEvent = (event: Event) => {
      const detail = (event as CustomEvent<BackButtonEventDetail>).detail;
      detail.register(priority, (next) => latest.current(next));
    };
    document.addEventListener('ionBackButton', onEvent);
    return () => document.removeEventListener('ionBackButton', onEvent);
  }, [priority]);
}

/**
 * Meldet den Zuhörer an, ohne den Capacitor die Taste verschluckt (Punkt 1
 * oben). Die eigentliche Arbeit machen die `useBackButton`-Handler - hier
 * bleibt der Rumpf bewusst leer, sonst liefe jeder Druck doppelt: einmal
 * über diesen Rückruf und einmal über Ionics `ionBackButton`.
 */
export function useHardwareBackButton() {
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let handle: PluginListenerHandle | null = null;
    let cancelled = false;

    void CapacitorApp.addListener('backButton', () => {
      /* absichtlich leer - siehe oben */
    }).then((registered) => {
      if (cancelled) void registered.remove();
      else handle = registered;
    });

    return () => {
      cancelled = true;
      void handle?.remove();
    };
  }, []);
}

/** Schliesst die App - nur nativ sinnvoll, im Browser gibt es das nicht. */
export function exitApp() {
  if (!Capacitor.isNativePlatform()) return;
  void CapacitorApp.exitApp();
}
